import React, { useState, useCallback } from "react";
import { Copy, Check } from "lucide-react";
import type { CopyableCredentials } from "@/types";

interface CopyCredentialsProps {
  credentials: CopyableCredentials;
  title?: string;
  className?: string;
  showLabels?: boolean;
}

/**
 * CopyCredentials - Reusable component for displaying and copying user credentials
 * Following SOLID principles:
 * - Single Responsibility: Handles only credential display and copying
 * - Open/Closed: Extensible through props
 * - Interface Segregation: Focused props for specific functionality
 */
export const CopyCredentials: React.FC<CopyCredentialsProps> = ({
  credentials,
  title = "User Credentials",
  className = "",
  showLabels = true,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Format credentials for copying (DRY principle)
  const formatCredentials = useCallback(
    (creds: CopyableCredentials): string => {
      return `email:${creds.email} | password:${creds.password}`;
    },
    []
  );

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      setIsLoading(true);
      const formattedText = formatCredentials(credentials);

      await navigator.clipboard.writeText(formattedText);
      setIsCopied(true);

      // Reset copied state after 3 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    } catch (error) {
      console.error("Failed to copy credentials:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = formatCredentials(credentials);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } finally {
      setIsLoading(false);
    }
  }, [credentials, formatCredentials]);

  return (
    <div className={`copy-credentials ${className}`}>
      {title && <h3 className="credentials-title">{title}</h3>}

      <div className="credentials-content">
        <div className="credentials-display">
          <div className="credential-item">
            {showLabels && <span className="credential-label">Email:</span>}
            <span className="credential-value email">{credentials.email}</span>
          </div>
          <div className="credential-item">
            {showLabels && <span className="credential-label">Password:</span>}
            <span className="credential-value password">
              {credentials.password}
            </span>
          </div>
        </div>

        <button
          onClick={handleCopy}
          disabled={isLoading}
          className={`copy-button ${isCopied ? "copied" : ""}`}
          title="Copy credentials to clipboard"
        >
          {isCopied ? (
            <>
              <Check size={16} />
              Copied!
            </>
          ) : (
            <>
              <Copy size={16} />
              {isLoading ? "Copying..." : "Copy"}
            </>
          )}
        </button>
      </div>

      <div className="copy-format-info">
        <small>
          Format:{" "}
          <code>
            email:{credentials.email} | password:{credentials.password}
          </code>
        </small>
      </div>
    </div>
  );
};
